import { useState, useEffect } from 'react'
import { formatVersion } from '../utils/version'
import { useParams, Link } from 'react-router-dom'
import { ArrowLeft, Server, Package, TrendingUp, AlertTriangle } from 'lucide-react'

interface AppInfo {
  app_id: string
  name: string
  description?: string
  status: string
  created_at: string
  updated_at: string
}

interface Stats {
  totalDevices: number
  activeDevices: number
  totalVersions: number
  latestVersion?: string
}

export default function AppDashboard() {
  const { appId } = useParams<{ appId: string }>()
  const [app, setApp] = useState<AppInfo | null>(null)
  const [stats, setStats] = useState<Stats | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    loadAppInfo()
    loadStats()
  }, [appId])

  const loadAppInfo = async () => {
    try {
      const response = await fetch(`/api/admin/apps/${appId}`)
      const data = await response.json()
      setApp(data)
    } catch (error) {
      console.error('加载应用信息失败:', error)
    }
  }

  const loadStats = async () => {
    try {
      const [devicesRes, versionsRes] = await Promise.all([
        fetch(`/api/admin/apps/${appId}/devices`),
        fetch(`/api/admin/apps/${appId}/versions`)
      ])
      
      const devicesData = await devicesRes.json()
      const versionsData = await versionsRes.json()
      
      setStats({
        totalDevices: devicesData.stats?.total || 0,
        activeDevices: devicesData.stats?.active || 0,
        totalVersions: versionsData.total || 0,
        latestVersion: versionsData.versions?.[0]?.version
      })
    } catch (error) {
      console.error('加载统计数据失败:', error)
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-gray-400">加载中...</div>
      </div>
    )
  }

  if (!app) {
    return (
      <div className="card text-center py-12">
        <h3 className="text-xl font-bold text-white mb-2">应用不存在</h3>
        <Link to="/apps" className="text-primary-400 hover:underline">
          返回应用列表
        </Link>
      </div>
    )
  }

  const navItems = [
    { path: `/apps/${appId}`, label: '概览', icon: TrendingUp },
    { path: `/apps/${appId}/devices`, label: '设备管理', icon: Server },
    { path: `/apps/${appId}/versions`, label: '版本管理', icon: Package },
    { path: `/apps/${appId}/gray-release`, label: '灰度发布', icon: AlertTriangle }
  ]

  return (
    <div className="space-y-6">
      {/* 返回按钮和应用信息 */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-4">
          <Link to="/apps" className="text-gray-400 hover:text-white transition-colors">
            <ArrowLeft size={20} />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-white">{app.name}</h1>
            <p className="text-gray-400 mt-1">{app.description || '暂无描述'}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className={`text-xs px-2 py-1 rounded-full ${
            app.status === 'active' ? 'text-green-400 bg-green-400/10' : 'text-gray-400 bg-gray-400/10'
          }`}>
            {app.status === 'active' ? '运行中' : app.status}
          </span>
        </div>
      </div>

      {/* 应用内导航 */}
      <nav className="flex gap-2 border-b border-dark-border">
        {navItems.map((item) => {
          const Icon = item.icon
          return (
            <Link
              key={item.path}
              to={item.path}
              className="flex items-center gap-2 px-4 py-2 text-sm text-gray-400 hover:text-white hover:bg-dark-border rounded-t-lg transition-colors"
            >
              <Icon size={16} />
              {item.label}
            </Link>
          )
        })}
      </nav>

      {/* 统计卡片 */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* 总设备数 */}
        <div className="card">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-lg bg-blue-500/20 flex items-center justify-center">
              <Server className="text-blue-400" size={24} />
            </div>
            <div>
              <p className="text-sm text-gray-400">总设备数</p>
              <p className="text-3xl font-bold text-white mt-1">{stats?.totalDevices || 0}</p>
            </div>
          </div>
        </div>

        {/* 活跃设备 */}
        <div className="card">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-lg bg-green-500/20 flex items-center justify-center">
              <TrendingUp className="text-green-400" size={24} />
            </div>
            <div>
              <p className="text-sm text-gray-400">活跃设备</p>
              <p className="text-3xl font-bold text-white mt-1">{stats?.activeDevices || 0}</p>
            </div>
          </div>
        </div>

        {/* 版本总数 */}
        <div className="card">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-lg bg-purple-500/20 flex items-center justify-center">
              <Package className="text-purple-400" size={24} />
            </div>
            <div>
              <p className="text-sm text-gray-400">版本总数</p>
              <p className="text-3xl font-bold text-white mt-1">{stats?.totalVersions || 0}</p>
            </div>
          </div>
        </div>

        {/* 最新版本 */}
        <div className="card">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-lg bg-yellow-500/20 flex items-center justify-center">
              <AlertTriangle className="text-yellow-400" size={24} />
            </div>
            <div>
              <p className="text-sm text-gray-400">最新版本</p>
              <p className="text-xl font-bold text-white mt-1 font-mono">{stats?.latestVersion ? formatVersion(parseInt(stats.latestVersion)) : '-'}</p>
            </div>
          </div>
        </div>
      </div>

      {/* 快速操作 */}
      <div className="card">
        <h3 className="text-lg font-semibold text-white mb-4">快速操作</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Link
            to={`/apps/${appId}/versions`}
            className="p-4 bg-dark-border rounded-lg hover:bg-primary-600/20 transition-colors"
          >
            <h4 className="font-medium text-white mb-1">📦 发布新版本</h4>
            <p className="text-sm text-gray-400">上传新固件并创建版本</p>
          </Link>
          
          <Link
            to={`/apps/${appId}/devices`}
            className="p-4 bg-dark-border rounded-lg hover:bg-primary-600/20 transition-colors"
          >
            <h4 className="font-medium text-white mb-1">🔍 查看设备</h4>
            <p className="text-sm text-gray-400">管理设备和查看状态</p>
          </Link>
          
          <Link
            to={`/apps/${appId}/gray-release`}
            className="p-4 bg-dark-border rounded-lg hover:bg-primary-600/20 transition-colors"
          >
            <h4 className="font-medium text-white mb-1">🎯 灰度发布</h4>
            <p className="text-sm text-gray-400">配置灰度发布策略</p>
          </Link>
        </div>
      </div>
    </div>
  )
}
